'use client';

import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { Slot } from '../slot';
import { PageContext } from './context/page-context';
import { pageContentRecipe } from './styles/page-content.styles';
import type { PageContentPropsInterface } from './types/page-content.types';

/**
 * Page content is a generic container that provides a consistent layout
 * for all `Page` components. It sets the max-width and defines
 * default padding and margins.
 *
 * You can use this component with your own custom components to make sure layouts are consistent.
 *
 * @since 8.15.0
 */
export const PageContent = forwardRef<HTMLDivElement, PageContentPropsInterface>(
  (props: PageContentPropsInterface, forwardedRef) => {
    const { asChild = false, children, className, css: cssProp, fluid, ...rest } = props;
    const context = useContext(PageContext);

    const styles = css(pageContentRecipe.raw({ fluid: fluid ?? context.fluid }), cssProp);

    const Component = asChild ? Slot : 'div';

    return (
      <Component
        data-c11n-component="Page.Content"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', 'c11n_page__content', styles, className)}
        ref={forwardedRef}
        {...rest}
      >
        {children}
      </Component>
    );
  },
);

PageContent.displayName = 'Page.Content';
